'use client';
import Link from 'next/link';
import { GlassCard } from '@/components/landing/v4-shared';
import { IndustryIcon } from '@/components/IndustryIcon';
import type { Industry } from '@/lib/industries';

/**
 * Industry tile for the /industries index grid.
 *
 * Renders inside a `<li>` (the parent grid uses `<ul>`). The whole card is
 * a single anchor — the `Link` wraps the GlassCard so any click within the
 * card hits the slug page. Hover is jade-tinted via GlassCard's accent.
 *
 * The View Transitions API is opted into via the `viewTransitionName`
 * inline style — when navigation lands on the slug page, the matching
 * `viewTransitionName` element morphs in. Engines without VT support
 * silently ignore the property.
 */
export function IndustryCard({ industry }: { industry: Industry }) {
  const href = `/industries/${industry.slug}`;
  return (
    <li>
      <Link
        href={href}
        data-testid={`industry-link-${industry.slug}`}
        className="industry-card-link"
        style={{
          display: 'block',
          textDecoration: 'none',
          color: 'inherit',
          height: '100%',
          // View Transitions API hint — the navigation-target hero icon
          // shares the same name and morphs across the cross-document
          // transition. Engines without support ignore this property.
          viewTransitionName: `industry-tile-${industry.slug}`,
        }}
      >
        <GlassCard
          accent="jade"
          hoverable
          highlightLine
          radius={18}
          padding={28}
          as="article"
          style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 48,
              height: 48,
              borderRadius: 14,
              background:
                'linear-gradient(135deg, rgba(185,246,192,0.18), rgba(15,106,74,0.16))',
              border: '1px solid rgba(185,246,192,0.22)',
              color: 'var(--jade-glow)',
              marginBottom: 18,
            }}
            aria-hidden="true"
          >
            <IndustryIcon name={industry.icon} size={24} />
          </div>
          <h2
            style={{
              fontFamily: 'var(--font-geist-sans)',
              fontSize: 19,
              fontWeight: 500,
              letterSpacing: '-0.01em',
              color: 'var(--paper)',
              margin: '0 0 8px',
              lineHeight: 1.2,
            }}
          >
            {industry.name}
          </h2>
          <p
            style={{
              fontFamily: 'var(--font-geist-sans)',
              fontSize: 13.5,
              lineHeight: 1.5,
              color: 'var(--dim)',
              margin: '0 0 18px',
              flex: 1,
            }}
          >
            {industry.tagline}
          </p>
          <span
            style={{
              fontFamily: 'var(--font-geist-mono)',
              fontSize: 11,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: 'var(--jade-glow)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            Learn more <span aria-hidden="true">→</span>
          </span>
        </GlassCard>
      </Link>
    </li>
  );
}
